import { useEffect, useState } from "react";

import { CheckForm } from "../../components/alle-check/CheckForm";
import { CheckFormStepper } from "../../components/alle-check/CheckFormStepper";
import { CheckResults } from "../../components/alle-check/CheckResults";
import { LiveSidebar } from "../../components/alle-check/LiveSidebar";
import { useAlleCheckMutation, type CheckResponse } from "../../lib/alle-check-client";
import { useLang, useT } from "../../i18n/language";
import { type RecommenderFormState } from "../../components/alle-check/recommender-input";

const HERO_IMAGE_URL =
  "https://cdn.prod.website-files.com/64b7e8bbb6f945a84ca508e1/6912fe041961e85d3e33c073_all-e-visual%20(2).avif";

const VIEW_MODE_KEY = "alle-check-view-mode";
type ViewMode = "list" | "stepper";

function readInitialMode(): ViewMode {
  if (typeof window === "undefined") return "stepper";
  const stored = window.localStorage.getItem(VIEW_MODE_KEY);
  if (stored === "list" || stored === "stepper") return stored;
  // Default voor alle viewports: stap-voor-stap. De lijstweergave blijft
  // bereikbaar via de toggle in de header (localStorage onthoudt de keuze).
  return "stepper";
}

export function CheckPage() {
  const t = useT();
  const { lang } = useLang();
  const mutation = useAlleCheckMutation(lang);
  const [resultBundle, setResultBundle] = useState<CheckResponse | null>(null);
  const [formState, setFormState] = useState<RecommenderFormState | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => readInitialMode());

  // Persist + react to changes from header toggle (custom event).
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onModeChange = (e: Event) => {
      const detail = (e as CustomEvent<ViewMode>).detail;
      if (detail === "list" || detail === "stepper") setViewMode(detail);
    };
    window.addEventListener("alle-check:view-mode", onModeChange);
    return () => window.removeEventListener("alle-check:view-mode", onModeChange);
  }, []);

  if (resultBundle) {
    return (
      <div className="container">
        <CheckResults
          result={resultBundle.result}
          hybrid={resultBundle.hybrid}
          headline={resultBundle.headline}
          submissionId={resultBundle.id}
          onReset={() => {
            setResultBundle(null);
            setFormState(null);
          }}
        />
      </div>
    );
  }

  const submitHandler = async (input: Parameters<Parameters<typeof CheckForm>[0]["onSubmit"]>[0]) => {
    const data = await mutation.mutateAsync(input);
    setResultBundle(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (viewMode === "stepper") {
    return (
      <>
        <section className="hero-section hero-section-compact">
          <div className="hero-content">
            <div className="hero-text">
              <div className="eyebrow">{t("All-Electric geschiktheidscheck")}</div>
              <h1 className="h1">{t("All-E Recommender")}</h1>
              <p className="subtitle">
                {t("Beantwoord een paar korte vragen over de woning. We doen meteen een passende aanbeveling: Quatt All-Electric Solo of Duo, of een Hybrid.")}
              </p>
            </div>
            <div className="hero-visual">
              <div className="glow-blob" />
              <img src={HERO_IMAGE_URL} alt="Quatt All-Electric" className="product-render" />
            </div>
          </div>
        </section>

        <div className="container stepper-container">
          {mutation.isError && (
            <div className="error-box" style={{ marginTop: 24 }}>
              {t("Kon de aanbeveling niet uitvoeren:")} {mutation.error.message}
            </div>
          )}
          <CheckFormStepper
            isSubmitting={mutation.isPending}
            onStateChange={setFormState}
            onSubmit={submitHandler}
          />
          {/* formState is collected for parity with list mode but the sidebar is hidden on stepper. */}
          {formState ? null : null}
        </div>
      </>
    );
  }

  return (
    <>
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <div className="eyebrow">{t("All-Electric geschiktheidscheck")}</div>
            <h1 className="h1">{t("All-E Recommender")}</h1>
            <p className="subtitle">
              {t("Welke heat pump past bij deze woning: All-Electric Solo of Duo? Vul in wat je weet en krijg meteen een aanbeveling plus aandachtspunten.")}
            </p>
          </div>
          <div className="hero-visual">
            <div className="glow-blob" />
            <img src={HERO_IMAGE_URL} alt="Quatt All-Electric" className="product-render" />
          </div>
        </div>
      </section>

      <div className="container">
        {mutation.isError && (
          <div className="error-box" style={{ marginTop: 24 }}>
            {t("Kon de aanbeveling niet uitvoeren:")} {mutation.error.message}
          </div>
        )}

        <div className="main-layout">
          <CheckForm
            isSubmitting={mutation.isPending}
            onStateChange={setFormState}
            onSubmit={submitHandler}
          />
          <LiveSidebar formState={formState} />
        </div>
      </div>
    </>
  );
}
